
import type { ChartOptions, TooltipItem } from 'chart.js'

import {
  BAR_CHART_CONFIG,
  CHART_OPTIONS_BASE,
  DONUT_CHART_CONFIG,
  ROTATION,
  SPACING,
  TYPOGRAPHY,
} from '../config/chartConfig'
import { getAssetCountLabel, getYAxisLabel } from '../config/chartLabels'
import { CHART_THEME } from '../config/chartTheme'

export type ChartItem = {
  name: string
  value: number
}

export function calculateBarScaleConfig(
  data: ChartItem[]
): { max: number; stepSize: number } {
  const maxValue = Math.max(...data.map((item) => Number(item.value) || 0), 0)
  const { scales, tickCount, stepRoundingFactor } = BAR_CHART_CONFIG

  if (maxValue <= scales.small.max) {
    return {
      max: scales.small.max,
      stepSize: scales.small.step,
    }
  }

  if (maxValue <= scales.medium.max) {
    return {
      max: scales.medium.max,
      stepSize: scales.medium.step,
    }
  }

  if (maxValue <= scales.large.max) {
    return {
      max: scales.large.max,
      stepSize: scales.large.step,
    }
  }

  const stepSize =
    Math.ceil(maxValue / tickCount / stepRoundingFactor) * stepRoundingFactor

  return {
    max: stepSize * tickCount,
    stepSize,
  }
}

export function isMobileViewport(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  return window.innerWidth < 768
}

export function buildBarChartOptions(data: ChartItem[]): ChartOptions<'bar'> {
  const scaleConfig = calculateBarScaleConfig(data)
  const isMobile = isMobileViewport()
  const responsiveConfig = isMobile
    ? BAR_CHART_CONFIG.responsive.mobile
    : BAR_CHART_CONFIG.responsive.desktop

  return {
    ...CHART_OPTIONS_BASE,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: CHART_THEME.tooltip.backgroundColor,
        padding: SPACING.tooltip,
        titleColor: CHART_THEME.tooltip.titleColor,
        bodyColor: CHART_THEME.tooltip.bodyColor,
        displayColors: true,
        titleFont: {
          family: TYPOGRAPHY.fontFamily,
          size: TYPOGRAPHY.sizes.large,
        },
        bodyFont: {
          family: TYPOGRAPHY.fontFamily,
          size: TYPOGRAPHY.sizes.large,
        },
        callbacks: {
          label: function (context: TooltipItem<'bar'>) {
            return `${context.label}: ${context.parsed.y ?? 0}`
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: scaleConfig.max,
        title: {
          display: true,
          text: getYAxisLabel(),
          color: CHART_THEME.text.color,
          font: {
            family: TYPOGRAPHY.fontFamily,
            size: TYPOGRAPHY.sizes.medium,
            weight: TYPOGRAPHY.weights.normal,
          },
        },
        ticks: {
          color: CHART_THEME.text.color,
          font: {
            family: TYPOGRAPHY.fontFamily,
            size: isMobile
              ? TYPOGRAPHY.sizes.mediumSmall
              : TYPOGRAPHY.sizes.medium,
          },
          stepSize: scaleConfig.stepSize,
        },
        grid: {
          display: false,
          drawBorder: false,
        },
      },
      x: {
        ticks: {
          color: CHART_THEME.text.color,
          font: {
            family: TYPOGRAPHY.fontFamily,
            size: isMobile ? TYPOGRAPHY.sizes.small : TYPOGRAPHY.sizes.mediumSmall,
          },
          maxRotation: isMobile ? ROTATION.mobile : ROTATION.desktop,
          minRotation: isMobile ? ROTATION.mobile : ROTATION.desktop,
          autoSkip: false,
          padding: isMobile ? SPACING.tick.mobile : SPACING.tick.desktop,
        },
        grid: {
          display: false,
        },
      },
    },
    barPercentage: responsiveConfig.barPercentage,
    categoryPercentage: responsiveConfig.categoryPercentage,
  }
}

export function buildDonutChartOptions(): ChartOptions<'doughnut'> {
  return {
    ...CHART_OPTIONS_BASE,
    plugins: {
      legend: {
        display: true,
        position: DONUT_CHART_CONFIG.legend.position,
        labels: {
          color: CHART_THEME.text.color,
          padding: DONUT_CHART_CONFIG.legend.padding,
          font: {
            family: TYPOGRAPHY.fontFamily,
            size: TYPOGRAPHY.sizes.medium,
          },
          usePointStyle: true,
          pointStyle: 'circle' as const,
        },
      },
      tooltip: {
        backgroundColor: CHART_THEME.tooltip.backgroundColor,
        padding: SPACING.tooltip,
        titleColor: CHART_THEME.tooltip.titleColor,
        bodyColor: CHART_THEME.tooltip.bodyColor,
        displayColors: true,
        titleFont: {
          family: TYPOGRAPHY.fontFamily,
          size: TYPOGRAPHY.sizes.large,
        },
        bodyFont: {
          family: TYPOGRAPHY.fontFamily,
          size: TYPOGRAPHY.sizes.large,
        },
      },
    },
    cutout: DONUT_CHART_CONFIG.cutout,
  }
}

export function calculateTotalAssets(data: ChartItem[]): number {
  return data.reduce((total, item) => total + Number(item.value || 0), 0)
}

export function getDonutCenterText(data: ChartItem[]): string {
  const total = calculateTotalAssets(data)
  return getAssetCountLabel(total)
}
